import React, { useState, useEffect } from "react";
import { isFirebaseConfigured } from "../firebase";
import { useFirestore } from "../contexts/FirestoreContext";
import TeamCrest from "./TeamCrest";
import { leagueLabel } from "../utils/tournament";

// T1's own crest (served from public/); constant since it's always our team.
const T1_LOGO = "/t1-logo.png";

const MatchCalendar: React.FC = () => {
  const { matches } = useFirestore();

  const MATCH_DURATION_MS = 4 * 60 * 60 * 1000;
  const now = new Date();
  const upcomingMatches = matches.filter((m) => {
    const matchStart = new Date(`${m.date}T${m.time}:00`);
    const matchEnd = new Date(matchStart.getTime() + MATCH_DURATION_MS);
    // Show if match hasn't ended yet, or if it's today and still active
    return matchEnd >= now || (m.isActive && new Date(m.date + "T23:59:59") >= now);
  });

  // Live countdown to nearest match
  const nearestMatch = upcomingMatches.length > 0 ? upcomingMatches[0] : null;
  const nearestMatchDate = nearestMatch?.date;
  const nearestMatchTime = nearestMatch?.time;
  const [countdown, setCountdown] = useState("");
  const [urgency, setUrgency] = useState<"far" | "soon" | "imminent" | "live">(
    "far"
  );

  // A match is "live" from its start until the max duration elapses.
  const isLiveMatch = (m: { date: string; time: string }) => {
    const start = new Date(`${m.date}T${m.time}:00`).getTime();
    return Date.now() >= start && Date.now() < start + MATCH_DURATION_MS;
  };

  useEffect(() => {
    if (!nearestMatchDate || !nearestMatchTime) return;

    const MATCH_DURATION_MS = 4 * 60 * 60 * 1000; // 4 hours max

    const update = () => {
      const matchDateTime = new Date(`${nearestMatchDate}T${nearestMatchTime}:00`);
      const diff = matchDateTime.getTime() - Date.now();

      if (diff <= 0 && Math.abs(diff) < MATCH_DURATION_MS && nearestMatch?.isActive !== false) {
        setCountdown("LIVE");
        setUrgency("live");
        return;
      }

      if (diff <= 0) {
        setCountdown("");
        setUrgency("far");
        return;
      }

      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);

      setCountdown(
        d > 0 ? `${d}d ${h}h ${m}m ${s}s` : h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`
      );
      setUrgency(diff < 5 * 60000 ? "imminent" : diff < 60 * 60000 ? "soon" : "far");
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [nearestMatchDate, nearestMatchTime]);

  return (
    <div className="w-full">
      <div className="glass-card rounded-2xl p-6 md:p-8">
        <h3 className="font-heading text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4 flex items-center gap-3">
          <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
          Lịch thi đấu
        </h3>

        {nearestMatch && countdown && (
          <div
            className={`text-center py-2.5 mb-4 rounded-xl border ${
              urgency === "imminent" || urgency === "live"
                ? "bg-primary/10 border-primary/25"
                : "bg-primary/5 border-primary/10"
            }`}
          >
            <span className="text-white/30 text-[10px] uppercase tracking-widest font-heading block mb-1">
              {urgency === "live" ? "Đang thi đấu" : "Trận tiếp theo trong"}
            </span>
            <span
              key={countdown}
              className={`countdown-tick inline-block font-heading font-bold text-base tracking-wider ${
                urgency === "live"
                  ? "text-green-400 animate-pulse"
                  : urgency === "imminent"
                  ? "text-red-300 countdown-imminent"
                  : urgency === "soon"
                  ? "text-red-400 countdown-soon"
                  : "text-primary"
              }`}
            >
              {countdown}
            </span>
          </div>
        )}

        {!isFirebaseConfigured ? (
          <div className="text-center py-6">
            <div className="text-white/10 text-4xl mb-3">📡</div>
            <p className="text-white/30 text-sm font-body">
              Chưa kết nối Firebase
            </p>
            <p className="text-white/15 text-xs mt-1 font-body">
              Thêm config vào .env
            </p>
          </div>
        ) : upcomingMatches.length === 0 ? (
          <div className="text-center py-6">
            <div className="text-white/10 text-4xl mb-3">⚔️</div>
            <p className="text-white/30 text-sm font-body">
              Chưa có trận sắp tới
            </p>
          </div>
        ) : (
          <div className="match-list space-y-3 max-h-[40vh] overflow-y-auto pr-1">
            {upcomingMatches.map((match, i) => {
              const matchDate = new Date(match.date + "T00:00:00");
              const isToday =
                matchDate.toDateString() === new Date().toDateString();
              const isLive = isLiveMatch(match);
              const isNext = i === 0 && !isLive;

              return (
                <div
                  key={match.id}
                  className={`group reveal-up relative p-4 rounded-xl border transition-all ${
                    isLive
                      ? "bg-primary/10 border-primary/40"
                      : isToday
                      ? "bg-primary/10 border-primary/30"
                      : "bg-white/[0.03] border-white/[0.06] hover:border-white/10"
                  }`}
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  {isLive && (
                    <div className="match-live pointer-events-none absolute inset-0 rounded-xl" />
                  )}
                  {/* Match accent bar (breathes for the next / live match) */}
                  <div
                    className={`absolute -left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r ${
                      isNext || isLive ? "accent-breathe" : "bg-primary/40"
                    }`}
                  />

                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-heading font-bold text-white text-base flex items-center gap-1.5">
                      <TeamCrest
                        name="T1"
                        logoUrl={T1_LOGO}
                        size={22}
                        className="transition-transform duration-200 group-hover:-translate-y-0.5"
                      />
                      T1
                      <span className="text-white/30 font-normal text-sm">
                        vs
                      </span>
                      <TeamCrest
                        name={match.opponent}
                        logoUrl={match.opponentLogo}
                        size={22}
                        className="transition-transform duration-200 group-hover:-translate-y-0.5"
                      />
                      {match.opponent}
                    </span>
                    {isLive ? (
                      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-green-400 font-heading font-bold">
                        <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                        LIVE
                      </span>
                    ) : match.isActive ? (
                      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-primary font-heading font-semibold">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                        Mở khấn
                      </span>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-2 text-white/40 text-xs font-body">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <span>
                      {isToday
                        ? "Hôm nay"
                        : matchDate.toLocaleDateString("vi-VN", {
                            weekday: "short",
                            day: "2-digit",
                            month: "2-digit",
                          })}
                    </span>
                    <span className="text-white/15">•</span>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12,6 12,12 16,14" />
                    </svg>
                    <span>{match.time}</span>
                    {leagueLabel(match.tournament) && (
                      <span className="shrink-0 px-1.5 py-0.5 rounded bg-primary/15 border border-primary/20 text-primary/90 text-[10px] font-heading font-bold uppercase tracking-wider">
                        {leagueLabel(match.tournament)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {upcomingMatches.length > 2 && (
              <div className="text-center text-white/20 text-xs font-body mt-2 md:hidden">
                +{upcomingMatches.length - 2} trận nữa
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchCalendar;
